import React, {useEffect,useState} from 'react';
import {useParams,Link} from 'react-router-dom';
import {Helmet} from 'react-helmet';
import api from '../services/apiService';
import SrrSyncStatus from './SrrSyncStatus';
import {usePerms} from '../hooks/useAuth';

const rateFields=[['srrDay','Tagespreis'],['srrDay3','Pro Tag ab 3 Tagen'],['srrDay5','Pro Tag ab 5 Tagen'],['srrDay10','Pro Tag ab 10 Tagen'],['srrHour','Stundenpreis']];
const initial={channelKey:'SRR',isActive:false,srrPriceMode:'request',titleOverride:'',descriptionOverride:'',notes:'',imageUrl:'',location:'',...Object.fromEntries(rateFields.map(([key])=>[key,'']))};
export default function ItemWebChannelSrrEdit(){
  const {itemId}=useParams(),{has}=usePerms();
  const [form,setForm]=useState(initial),[item,setItem]=useState(null);
  const [loading,setLoading]=useState(true),[saving,setSaving]=useState(false),[error,setError]=useState(''),[message,setMessage]=useState(''),[refresh,setRefresh]=useState(0),[preview,setPreview]=useState(null);
  const change=(key,value)=>setForm(current=>({...current,[key]:value}));
  useEffect(()=>{
    let active=true;
    Promise.all([api.get(`/items/${itemId}`),api.get(`/item-web-channels/item/${itemId}`)]).then(([i,c])=>{
      if(!active)return;
      setItem(i.data);
      const channel=(c.data||[]).find(value=>value.channelKey==='SRR');
      setForm({...initial,...channel});
    }).catch(e=>{if(active)setError(e.response?.data?.error||'SRR-Daten konnten nicht geladen werden.');}).finally(()=>{if(active)setLoading(false)});
    return()=>{active=false};
  },[itemId]);
  const save=async(event,deactivate=false)=>{
    event.preventDefault();setSaving(true);setError('');setMessage('');
    try{
      const payload=deactivate?{channelKey:'SRR',isActive:false}:Object.fromEntries(Object.keys(initial).map(key=>[key,form[key]??initial[key]]));
      const result=await api.post(`/item-web-channels/item/${itemId}`,payload);
      setForm({...initial,...result.data.data});setRefresh(v=>v+1);setPreview(null);
      setMessage(deactivate?'SRR deaktiviert. Die Löschung des Inserats auf SRR ist vorgemerkt.':'SRR gespeichert. Änderungen werden bei aktivem Abgleich übertragen.');
    }catch(e){setError(e.response?.data?.error||'SRR konnte nicht gespeichert werden.')}
    finally{setSaving(false)}
  };
  const showPreview=async()=>{
    try{const result=await api.get(`/srr-integration/preview/${itemId}`);setPreview(result.data);setError('')}
    catch(e){setPreview(null);setError(e.response?.data?.error||'Vorschau fehlgeschlagen.')}
  };
  if(loading)return <p>Lade SRR-Web-Channel …</p>;
  return <div className="form">
    <Helmet><title>SmartRentalReady – Artikel bearbeiten</title></Helmet>
    <h2>SmartRentalReady: {item?.name||`Artikel ${itemId}`}</h2>
    <p><Link to="/itemWebChannelListSRR">Zur SRR-Liste</Link>{has('/srr-integration')&&<> · <Link to="/srr-integration">Verbindung und Feldfreigaben</Link></>}</p>
    {error&&<p role="alert">{error}</p>}{message&&<p role="status">{message}</p>}
    <SrrSyncStatus itemId={itemId} refresh={refresh}/>
    <form onSubmit={save}>
      <label><input type="checkbox" checked={!!form.isActive} onChange={e=>change('isActive',e.target.checked)}/> Auf SmartRentalReady veröffentlichen</label>
      <div className="form-row">
        <div className="col">
          <h3>Maschine und Standort</h3>
          <label>Titel<input value={form.titleOverride??''} onChange={e=>change('titleOverride',e.target.value)} maxLength={160} placeholder="Hersteller und Typ / Artikelname verwenden"/></label>
          <label>Beschreibung<textarea value={form.descriptionOverride??''} onChange={e=>change('descriptionOverride',e.target.value)} maxLength={12000} rows={7} placeholder="Artikelbeschreibung verwenden"/></label>
          <label>Standort<input value={form.location??''} onChange={e=>change('location',e.target.value)} maxLength={120} placeholder="SRR-Standort des Artikels verwenden"/></label>
        </div>
        <div className="col">
          <h3>Mietpreise netto in EUR</h3>
          <label>Preisangabe<select value={form.srrPriceMode||'request'} onChange={e=>change('srrPriceMode',e.target.value)}><option value="request">Preis auf Anfrage</option><option value="rates">Mietpreise angeben</option></select></label>
          {form.srrPriceMode==='rates'&&<>
            {rateFields.map(([key,label])=><label key={key}>{label}<input inputMode="decimal" value={form[key]??''} onChange={e=>change(key,e.target.value)} placeholder="z. B. 95,00"/></label>)}
            <p>Mindestens Tages- oder Stundenpreis angeben. Staffeln sind Tagespreise. Leere Felder werden nicht angezeigt; 0,00 ist ein tatsächlicher Preis.</p>
          </>}
        </div>
        <div className="col">
          <h3>Bild und Ausstattung</h3>
          <label>Bildadresse<input type="url" value={form.imageUrl??''} onChange={e=>change('imageUrl',e.target.value)} maxLength={2000} placeholder="https://…"/></label>
          <p>Ohne eigene Adresse wird das SRR-Bild des Artikels, danach dessen HP-Bild verwendet. Der Bild-Host muss auf SmartRentalReady freigegeben sein.</p>
          <label>Ausstattung / öffentliche Notizen<textarea value={form.notes??''} onChange={e=>change('notes',e.target.value)} maxLength={300} rows={4}/></label>
        </div>
      </div>
      <button type="submit" className="submit-button" disabled={saving}>{saving?'Speichert …':'SRR-Web-Channel speichern'}</button>
      {form.isActive&&<button type="button" onClick={e=>save(e,true)} disabled={saving}>Veröffentlichung zurückziehen</button>}
    </form>
    {has('/srr-integration')&&<section><h3>Übertragungsvorschau</h3><p>Verwendet die zuletzt gespeicherten Angaben und Feldfreigaben.</p><button type="button" onClick={showPreview}>Vorschau prüfen</button>{preview&&<pre style={{whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>{JSON.stringify(preview,null,2)}</pre>}</section>}
  </div>;
}
